import { AxiosResponse } from 'axios';
import { DOMUtil } from '../dom';
import { HttpService } from '../http';
import { CountryCode } from './country-code.enum';
import { LanguageCode } from './language-code.enum';
import { LocaleCode } from './locale-code.enum';
import { ITranslationData } from './translation-data.definition';
import { WindowWithTranslationGlobals } from './translation-globals.window-definition';
import { TranslationKey } from './translation-key.enum';
import { TranslationServiceConfiguration } from './translation-service.configuration';
import { WindowWithTranslationService } from './translation-service.window-definition';
import { TranslationsJson } from './translations-json.dto';

export class TranslationService {

    static readonly identifier = '$translationService';

    static decorateParams(translation: string, data: ITranslationData): string {
        let map: Map<string, string>;
        const start = data.identifierStart ? data.identifierStart : '{var}';
        const end = data.identifierEnd ? data.identifierEnd : '{/var}';

        if (data.keyValues !== undefined) {
            map = new Map(Object.entries(JSON.parse(data.keyValues)));
            map.forEach((value, key) => {
                translation = translation.replace(new RegExp(`${start}${key}${end}`, 'g'), value);
            });
        }

        if (this.globalVarRegEx.test(translation)) {
            Object.keys(this.globalVariables).forEach(varKey => {
                const variable = `%${varKey}%`;

                if (translation.indexOf(variable) !== -1) {
                    translation = translation.replace(new RegExp(variable, 'g'), this.globalVariables[varKey]);
                }
            });
        }

        return translation;
    }

    static getConfiguration(documentRef: Document, scriptName: string): TranslationServiceConfiguration {
        const radix: number = 10;
        const scriptElement = DOMUtil.getInjectedConfigurationScript(documentRef, scriptName) as HTMLScriptElement;
        if (!scriptElement) {
            throw new Error(`No element with ${scriptName} in src`);
        }

        const staticUrl: string | null = scriptElement.getAttribute('data-static');
        scriptElement.removeAttribute('data-static');

        if (!staticUrl) {
            throw new Error(`No data-static attribute on ${scriptName} element`);
        }

        const clientIdAttr: string | null = scriptElement.getAttribute('data-pool-id');
        if (!clientIdAttr) {
            throw new Error(`No data-pool-id attribute on ${scriptName} element`);
        }

        const clientId: number = parseInt(clientIdAttr, radix);
        scriptElement.removeAttribute('data-pool-id');

        return {
            staticUrl,
            clientId,
        };

    }

    static getInstance(configuration: TranslationServiceConfiguration): TranslationService {
        if (this.instance) {
            return this.instance;
        }

        const windowInstance = this.getDOMInstance();
        if (windowInstance && windowInstance.configuration.clientId === configuration.clientId) {
            return windowInstance;
        }

        this.instance = new TranslationService(configuration);
        (window as WindowWithTranslationService & typeof globalThis).TranslationService = this.instance;

        return this.instance;
    }

    static getIsoCurrencyFor(code: string) {
        if (code.toUpperCase() === 'FT') {
            return 'HUF';
        }

        if (code.toUpperCase() === 'KN') {
            return 'HRK';
        }

        return code;
    }

    static getLocale(): LocaleCode {
        const htmlElement = document.querySelector('body');
        if (!htmlElement) {
            return LocaleCode.English;
        }

        let localeCode: LocaleCode = htmlElement.getAttribute('data-locale') as LocaleCode;
        localeCode = localeCode.replace('_', '-') as LocaleCode;

        if (localeCode === undefined) {
            return LocaleCode.English;
        }

        return localeCode;
    }

    static getLanguageCode(): LanguageCode {
        const htmlElement = document.querySelector('html');
        if (!htmlElement) {
            return LanguageCode.English;
        }

        const languageCode: LanguageCode = htmlElement.getAttribute('lang') as LanguageCode;
        if (languageCode === undefined) {
            return LanguageCode.English;
        }

        return languageCode;
    }

    static getCountryCode(): CountryCode {
        return Object.entries(LanguageCode)
            .filter((value) => value[1] === this.getLanguageCode())
            .reduce((previousValue: CountryCode, currentValue: [string, string]): CountryCode => {
                const index: string = currentValue[0];
                return Object(CountryCode)[index] as CountryCode;
            }, CountryCode.English);
    }

    private static getDOMInstance(): TranslationService | undefined {
        const win: WindowWithTranslationService = window as WindowWithTranslationService & typeof globalThis;

        if (!win) {
            return undefined;
        }

        if (win.TranslationService === undefined) {
            return undefined;
        }

        return win.TranslationService;
    }

    private static globalVariables: { [key: string]: string } = (window as WindowWithTranslationGlobals & typeof globalThis).globalTranslationVariables;

    private static globalVarRegEx = /(\w+)/g;

    private static instance: TranslationService;

    private readonly endpoint: string;

    private activePromise: Promise<AxiosResponse<TranslationsJson>> | null = null;

    private http: HttpService;

    private translations: TranslationsJson | null = null;

    constructor(private configuration: TranslationServiceConfiguration) {
        this.http = HttpService.getInstance(configuration);
        this.endpoint = `${this.configuration.staticUrl}/static/translations/` +
            `${TranslationService.getLanguageCode()}.translation.json`;
    }

    async getTranslations(): Promise<TranslationsJson> {
        if (this.activePromise) {
            await this.activePromise;
        }

        if (this.translations !== null) {
            return Promise.resolve(this.translations);
        }

        return this.requestTranslations();
    }

    async getTranslationFor(translationKey: TranslationKey): Promise<string> {
        const translations = await this.getTranslations();
        const hit = translations[translationKey];

        return hit ? hit : `i18n:missing:${translationKey}`;
    }

    getAvailableTranslations(): TranslationsJson | null {
        return this.translations;
    }

    getAvailableTranslationFor(translationKey: TranslationKey): string {
        const translations = this.getAvailableTranslations();
        const hit = translations ? translations[translationKey] : null;

        return hit ? hit : ``;
    }

    private async requestTranslations(): Promise<TranslationsJson> {
        try {
            const requestPromise = this.activePromise = this.http.get<TranslationsJson>(this.endpoint);
            const response: AxiosResponse<TranslationsJson> = await requestPromise;

            this.translations = response.data;
            this.activePromise = null;

            return Promise.resolve(response.data);
        } catch (e) {
            if (this.translations !== null && Object.values(this.translations).length === 0) {
                this.translations = {};
            }

            return Promise.reject(`Missing translation definitions for ${TranslationService.getLanguageCode()}`);
        }
    }

}
